/**
 * Iter-129 — Single source of truth for scene ordering, display, and
 * search matching across every screen that lists scenes (Cast,
 * Character detail, On Set, Fittings, Scene detail, Image detail,
 * ScenesHub).
 *
 * Three things this file owns:
 *
 *   1. `compareScenes(a, b)` — canonical sort comparator. Sorts by
 *      episode (numeric ascending, then alpha, then empty/null last)
 *      and within an episode by scene_number (using order_index as a
 *      hint when set, then a natural-numeric sort on scene_number).
 *
 *   2. `formatSceneNumber(scene)` — returns the display label.
 *      Episodic projects use `"<EP>.<SCENE>"` (e.g. "3.1", "3.2");
 *      single-episode / film projects fall back to the raw scene
 *      number. This is the consistent on-screen format pilots asked
 *      for.
 *
 *   3. `sceneMatchesQuery(scene, query)` — search-tolerant matcher.
 *      Accepts the raw scene number ("1"), the episodic combined
 *      number ("3.1"), or any leading prefix ("3.", "3.1").
 *
 * No backend changes — these helpers live entirely in the FE so we
 * don't migrate stored scene_number values.
 */

const SCENE_NUM_KEY_RE = /^([A-Z]?)(\d{1,4})([A-Z]?)\s*$/;

export function compareEpisodes(a, b) {
    const ae = a == null || a === "" ? null : String(a);
    const be = b == null || b === "" ? null : String(b);
    if (ae == null && be == null) return 0;
    if (ae == null) return 1; // empty episodes go last
    if (be == null) return -1;
    const an = Number(ae);
    const bn = Number(be);
    const aNum = !Number.isNaN(an);
    const bNum = !Number.isNaN(bn);
    if (aNum && bNum) return an - bn;
    if (aNum) return -1;
    if (bNum) return 1;
    return ae.localeCompare(be);
}

// Iter-155A.4 — Screenplay-number key mirroring the backend's
// `_scene_number_sort_key` so that 52A sits between 52 and 53 and A53
// sits between 52B and 53. Hollywood convention:
//   - Prefix-letter scenes (A53) are insertions BEFORE the base → bucket 0.
//   - Suffix-letter scenes (52A) are insertions AFTER the base → bucket 1.
//   - Bare number ("52") sorts as (52, 1, "", "").
// Unparseable values sink to the bottom rather than risk reordering
// legitimate entries above them. Returned tuple is comparable element
// by element via compareKey().
function sceneNumberSortKey(sn) {
    if (sn == null) return [10 ** 9, 1, "", ""];
    const s = String(sn).trim().toUpperCase();
    if (!s) return [10 ** 9, 1, "", ""];
    const m = s.match(SCENE_NUM_KEY_RE);
    if (!m) return [10 ** 9, 1, "", s];
    const prefix = m[1];
    const n = parseInt(m[2], 10);
    const suffix = m[3];
    if (Number.isNaN(n)) return [10 ** 9, 1, "", s];
    if (prefix) return [n, 0, prefix, ""];
    return [n, 1, "", suffix];
}

function compareKey(a, b) {
    for (let i = 0; i < a.length; i += 1) {
        const av = a[i];
        const bv = b[i];
        if (av === bv) continue;
        if (typeof av === "number" && typeof bv === "number") return av - bv;
        return String(av).localeCompare(String(bv));
    }
    return 0;
}

export function compareSceneNumbers(a, b) {
    return compareKey(sceneNumberSortKey(a), sceneNumberSortKey(b));
}

export function compareScenes(a, b) {
    const ep = compareEpisodes(a?.episode, b?.episode);
    if (ep !== 0) return ep;
    // Iter-155A.4 — screenplay-number key wins over order_index so
    // revision-inserted scenes (52A get order_index = max+1) appear
    // BETWEEN 52 and 53, not at the bottom of the list. order_index
    // is used only as a deterministic tiebreaker when the screenplay
    // key is identical (unparseable or duplicate numbers).
    const sn = compareSceneNumbers(a?.scene_number, b?.scene_number);
    if (sn !== 0) return sn;
    const ao = a?.order_index;
    const bo = b?.order_index;
    if (typeof ao === "number" && typeof bo === "number") return ao - bo;
    return 0;
}

/**
 * Stable sort helper — returns a new array.
 */
export function sortScenes(scenes) {
    return [...(scenes || [])].sort(compareScenes);
}

/**
 * Decide whether a list of scenes belongs to an episodic project.
 * Used by formatSceneNumber so single-episode / film projects don't
 * accidentally show "1.1" style labels.
 */
export function isEpisodicScenes(scenes) {
    return Array.isArray(scenes) && scenes.some((s) => s && s.episode);
}

/**
 * Canonical scene-number label used everywhere the user sees a scene.
 *
 *   episode=3, scene_number="1"  → "3.1"
 *   episode="Pilot", scene_number="1" → "Pilot.1"
 *   episode=null, scene_number="14A" → "14A"
 *
 * `forceEpisodic`: if you already know the surrounding context is
 * episodic (e.g. mid-render after `isEpisodicScenes(allScenes)`), pass
 * true so a scene that happens to have no episode still shows its raw
 * number without confusing the user.
 */
export function formatSceneNumber(scene, { forceEpisodic = false } = {}) {
    if (!scene) return "";
    const ep = scene.episode;
    const sn = (scene.scene_number || "").toString();
    if (ep == null || ep === "") return sn;
    if (!sn) return String(ep);
    // Avoid double-prefixing if the stored scene_number already starts
    // with the episode label (e.g. legacy data "3.1").
    const epStr = String(ep);
    if (sn.startsWith(`${epStr}.`)) return sn;
    return `${epStr}.${sn}`;
    // forceEpisodic is intentionally unused in the current branch — it
    // exists for future render contexts where caller wants to opt in
    // even when the individual scene lacks an episode. Keeping the
    // signature stable so callers don't churn.
    // eslint-disable-next-line no-unused-vars
}

/**
 * Search-tolerant matcher. Returns true if `query` looks like it
 * targets this scene's number — supports raw, dotted, partial prefix.
 *
 *   "1"   → matches scene_number=1 OR episode.scene starting with "1"
 *   "3."  → matches episode 3 scenes
 *   "3.1" → matches episode 3 scene 1 exactly OR its prefix
 */
export function sceneNumberMatches(scene, query) {
    if (!query) return false;
    const q = String(query).trim().toLowerCase();
    if (!q) return false;
    const sn = (scene?.scene_number || "").toString().toLowerCase();
    const ep = scene?.episode == null ? "" : String(scene.episode).toLowerCase();
    const combined = ep && sn ? `${ep}.${sn}` : sn || ep;
    return (
        sn.startsWith(q) ||
        combined.startsWith(q) ||
        combined === q ||
        sn === q
    );
}
